import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getPFSummary, getESISummary, getPTSummary, getTDSSummary,
  downloadCompliance, getClients
} from '../services/api';
import { Download, FileText, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmt  = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString('en-IN');

// ── Shared download helper ─────────────
const useDownload = () => {
  const [loading, setLoading] = useState(false);

  const download = async (apiPath, params, label) => {
    setLoading(true);
    try {
      const res  = await downloadCompliance(apiPath, params);
      const disp = res.headers['content-disposition'] || '';
      const name = disp.match(/filename="(.+)"/)?.[1] || label;
      const url  = URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${name} downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Download failed');
    } finally {
      setLoading(false);
    }
  };

  return { download, loading };
};

// ── Stat tiles ─────────────────────────
const StatTile = ({ label, value, sub, color = 'text-gray-900' }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>₹{fmtN(Math.round(value || 0))}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

// ── Empty state ─────────────────────────
const Empty = ({ msg }) => (
  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
    <AlertCircle size={28} className="mx-auto text-gray-200 mb-3" />
    <p className="text-sm text-gray-400">{msg}</p>
    <p className="text-xs text-gray-300 mt-1">Process payroll for this period first.</p>
  </div>
);

// ════════════════════════════════════════
//  PF TAB
// ════════════════════════════════════════
const PFTab = ({ month, year, clientId }) => {
  const { download, loading } = useDownload();

  const { data, isLoading } = useQuery({
    queryKey: ['compliance-pf', month, year, clientId],
    queryFn : () => getPFSummary({ month, year, client_id: clientId || undefined }).then(r => r.data.data)
  });

  const params = { month, year, ...(clientId ? { client_id: clientId } : {}) };

  if (isLoading) return <div className="text-center py-12 text-gray-400">Loading PF data...</div>;
  if (!data?.rows?.length) return <Empty msg="No payroll records found for this period." />;

  const { rows, totals } = data;

  return (
    <div className="space-y-4">
      {/* Tiles */}
      <div className="grid grid-cols-4 gap-4">
        <StatTile label="Employees" value={null} sub={`${data.count} covered`}
          color="text-blue-600" />
        <StatTile label="EPF Wages" value={totals.epf_wages} color="text-gray-900" />
        <StatTile label="Employee PF" value={totals.pf_employee} color="text-red-600" />
        <StatTile label="Employer PF" value={totals.pf_employer} color="text-orange-600" />
      </div>

      {/* Download ECR */}
      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-400">
          Total remittance: ₹{fmt(totals.pf_employee + totals.pf_employer)}
          <span className="ml-3 text-gray-300">(Employee ₹{fmt(totals.pf_employee)} + Employer ₹{fmt(totals.pf_employer)})</span>
        </p>
        <button onClick={() => download('/compliance/pf/ecr', params, `ECR_${MONTHS[month-1]}_${year}.txt`)}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Download size={15} />
          Download ECR File
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Employee','UAN','Gross Wages','EPF Wages','EPS Wages','Employee PF','Employer PF','NCP Days'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-400">{r.employee_code}</p>
                </td>
                <td className="px-4 py-3 text-gray-600 font-mono text-xs">{r.uan}</td>
                <td className="px-4 py-3 text-gray-700">₹{fmtN(r.gross_wages)}</td>
                <td className="px-4 py-3 text-gray-700">₹{fmtN(r.epf_wages)}</td>
                <td className="px-4 py-3 text-gray-700">₹{fmtN(r.eps_wages)}</td>
                <td className="px-4 py-3 font-medium text-red-600">₹{fmtN(r.pf_employee)}</td>
                <td className="px-4 py-3 font-medium text-orange-600">₹{fmtN(r.pf_employer)}</td>
                <td className="px-4 py-3 text-gray-500">{Math.round(r.ncp_days)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              <td className="px-4 py-3 font-semibold text-gray-700" colSpan={2}>Total ({data.count})</td>
              <td className="px-4 py-3 font-semibold text-gray-900">₹{fmtN(totals.gross_wages)}</td>
              <td className="px-4 py-3 font-semibold text-gray-900">₹{fmtN(totals.epf_wages)}</td>
              <td />
              <td className="px-4 py-3 font-semibold text-red-600">₹{fmtN(totals.pf_employee)}</td>
              <td className="px-4 py-3 font-semibold text-orange-600">₹{fmtN(totals.pf_employer)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════
//  ESI TAB
// ════════════════════════════════════════
const ESITab = ({ month, year, clientId }) => {
  const { download, loading } = useDownload();

  const { data, isLoading } = useQuery({
    queryKey: ['compliance-esi', month, year, clientId],
    queryFn : () => getESISummary({ month, year, client_id: clientId || undefined }).then(r => r.data.data)
  });

  const params = { month, year, ...(clientId ? { client_id: clientId } : {}) };

  if (isLoading) return <div className="text-center py-12 text-gray-400">Loading ESI data...</div>;
  if (!data?.rows?.length) return <Empty msg="No ESI-eligible employees for this period." />;

  const { rows, totals } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-1">Eligible Employees</p>
          <p className="text-2xl font-bold text-blue-600">{data.count}</p>
          <p className="text-xs text-gray-400 mt-1">{data.non_eligible} above ₹21,000 threshold</p>
        </div>
        <StatTile label="Gross Wages (ESI)" value={totals.gross_wages} />
        <StatTile label="Employee Contribution (0.75%)" value={totals.esic_employee} color="text-red-600" />
        <StatTile label="Employer Contribution (3.25%)" value={totals.esic_employer} color="text-orange-600" />
      </div>

      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-400">
          Total ESI remittance: <strong>₹{fmt(totals.total_esic)}</strong>
        </p>
        <button onClick={() => download('/compliance/esi/return', params, `ESI_Return_${MONTHS[month-1]}_${year}.csv`)}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Download size={15} />
          Download ESI Return
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Employee','ESIC Number','Gross Wages','Employee (0.75%)','Employer (3.25%)','Total'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-400">{r.employee_code}</p>
                </td>
                <td className="px-4 py-3 text-gray-600 font-mono text-xs">{r.esic_number}</td>
                <td className="px-4 py-3 text-gray-700">₹{fmtN(r.gross_wages)}</td>
                <td className="px-4 py-3 text-red-600 font-medium">₹{fmt(r.esic_employee)}</td>
                <td className="px-4 py-3 text-orange-600 font-medium">₹{fmt(r.esic_employer)}</td>
                <td className="px-4 py-3 font-semibold text-gray-900">₹{fmt(r.total_esic)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              <td className="px-4 py-3 font-semibold text-gray-700" colSpan={2}>Total</td>
              <td className="px-4 py-3 font-semibold">₹{fmtN(totals.gross_wages)}</td>
              <td className="px-4 py-3 font-semibold text-red-600">₹{fmt(totals.esic_employee)}</td>
              <td className="px-4 py-3 font-semibold text-orange-600">₹{fmt(totals.esic_employer)}</td>
              <td className="px-4 py-3 font-bold text-gray-900">₹{fmt(totals.total_esic)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════
//  PT TAB
// ════════════════════════════════════════
const PTTab = ({ month, year, clientId }) => {
  const { download, loading } = useDownload();

  const { data, isLoading } = useQuery({
    queryKey: ['compliance-pt', month, year, clientId],
    queryFn : () => getPTSummary({ month, year, client_id: clientId || undefined }).then(r => r.data.data)
  });

  const params = { month, year, ...(clientId ? { client_id: clientId } : {}) };

  if (isLoading) return <div className="text-center py-12 text-gray-400">Loading PT data...</div>;
  if (!data?.rows?.length) return <Empty msg="No Professional Tax records for this period." />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-1">PT Deducted From</p>
          <p className="text-2xl font-bold text-blue-600">{data.count} employees</p>
        </div>
        <StatTile label="Total PT Payable" value={data.total_pt} color="text-purple-600" />
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-2">Slab Breakdown</p>
          {Object.entries(data.slabs || {}).filter(([, v]) => v > 0).map(([slab, count]) => (
            <div key={slab} className="flex justify-between text-xs text-gray-600 mb-0.5">
              <span>{slab}</span><span className="font-medium">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={() => download('/compliance/pt/challan', params, `PT_Challan_${MONTHS[month-1]}_${year}.csv`)}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Download size={15} />
          Download PT Challan
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Employee','Gross Salary','PT Amount'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-400">{r.employee_code}</p>
                </td>
                <td className="px-4 py-3 text-gray-700">₹{fmtN(r.gross_salary)}</td>
                <td className="px-4 py-3 font-semibold text-purple-600">₹{fmt(r.pt_amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              <td className="px-4 py-3 font-semibold text-gray-700">Total</td>
              <td />
              <td className="px-4 py-3 font-bold text-purple-600">₹{fmt(data.total_pt)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════
//  TDS TAB
// ════════════════════════════════════════
const TDSTab = ({ quarter, year, clientId }) => {
  const { download, loading } = useDownload();

  const { data, isLoading } = useQuery({
    queryKey: ['compliance-tds', quarter, year, clientId],
    queryFn : () => getTDSSummary({ quarter, year, client_id: clientId || undefined }).then(r => r.data.data)
  });

  const params = { quarter, year, ...(clientId ? { client_id: clientId } : {}) };

  if (isLoading) return <div className="text-center py-12 text-gray-400">Loading TDS data...</div>;
  if (!data?.rows?.length) return <Empty msg="No payroll records found for this quarter." />;

  const { rows, totals } = data;

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center gap-3">
        <FileText size={16} className="text-blue-500 flex-shrink-0" />
        <p className="text-sm text-blue-700">
          <strong>Form 24Q — Q{data.quarter} {data.year}</strong> ({data.period}) ·
          Quarterly TDS return for salary payments under Section 192.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-1">Employees Covered</p>
          <p className="text-2xl font-bold text-blue-600">{data.count}</p>
        </div>
        <StatTile label="Total Gross Salary (Quarter)" value={totals.total_gross} />
        <StatTile label="TDS Deducted" value={totals.total_tds}
          sub={totals.total_tds === 0 ? 'No TDS deducted — salaries likely below threshold' : ''}
          color={totals.total_tds > 0 ? 'text-red-600' : 'text-gray-400'} />
      </div>

      <div className="flex justify-end">
        <button onClick={() => download('/compliance/tds/24q', params, `TDS_24Q_Q${quarter}_${year}.csv`)}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Download size={15} />
          Download 24Q CSV
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Employee','PAN','Months Covered','Gross Salary','PF Deducted','TDS Deducted'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-400">{r.employee_code}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.pan}</td>
                <td className="px-4 py-3 text-gray-600">{r.months.length} month{r.months.length !== 1 ? 's' : ''}</td>
                <td className="px-4 py-3 text-gray-900">₹{fmtN(Math.round(r.total_gross))}</td>
                <td className="px-4 py-3 text-orange-600">₹{fmtN(Math.round(r.total_pf))}</td>
                <td className="px-4 py-3 font-medium text-red-600">
                  {r.total_tds > 0 ? `₹${fmtN(Math.round(r.total_tds))}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              <td className="px-4 py-3 font-semibold text-gray-700" colSpan={3}>Total</td>
              <td className="px-4 py-3 font-semibold">₹{fmtN(Math.round(totals.total_gross))}</td>
              <td className="px-4 py-3 font-semibold text-orange-600">₹{fmtN(Math.round(totals.total_pf))}</td>
              <td className="px-4 py-3 font-bold text-red-600">
                {totals.total_tds > 0 ? `₹${fmtN(Math.round(totals.total_tds))}` : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════
const StatutoryCompliance = () => {
  const [tab,      setTab]      = useState('pf');
  const [month,    setMonth]    = useState(new Date().getMonth() + 1);
  const [year,     setYear]     = useState(new Date().getFullYear());
  const [quarter,  setQuarter]  = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [clientId, setClientId] = useState('');

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 100 }).then(r => r.data.data)
  });

  const isTDS   = tab === 'tds';
  const curYear = new Date().getFullYear();

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Statutory Compliance</h1>
          <p className="text-sm text-gray-400 mt-0.5">PF · ESI · Professional Tax · TDS</p>
        </div>
        <div className="flex gap-2">
          <select value={clientId} onChange={e => setClientId(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          {!isTDS ? (
            <>
              <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
              <select value={year} onChange={e => setYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {[curYear-1, curYear, curYear+1].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          ) : (
            <>
              <select value={quarter} onChange={e => setQuarter(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value={1}>Q1 (Apr–Jun)</option>
                <option value={2}>Q2 (Jul–Sep)</option>
                <option value={3}>Q3 (Oct–Dec)</option>
                <option value={4}>Q4 (Jan–Mar)</option>
              </select>
              <select value={year} onChange={e => setYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {[curYear-1, curYear, curYear+1].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* Period label */}
      <div className="text-xs text-gray-400">
        {isTDS
          ? `Quarter ${quarter} — ${year} (Indian financial year basis)`
          : `${MONTHS_FULL[month-1]} ${year}`}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { id: 'pf',  label: 'PF / EPF'           },
          { id: 'esi', label: 'ESI'                 },
          { id: 'pt',  label: 'Prof. Tax'           },
          { id: 'tds', label: 'TDS / Form 24Q'      }
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'pf'  && <PFTab  month={month} year={year} clientId={clientId} />}
      {tab === 'esi' && <ESITab month={month} year={year} clientId={clientId} />}
      {tab === 'pt'  && <PTTab  month={month} year={year} clientId={clientId} />}
      {tab === 'tds' && <TDSTab quarter={quarter} year={year} clientId={clientId} />}
    </div>
  );
};

export default StatutoryCompliance;
